import { Button, makeStyles, tokens } from '@fluentui/react-components';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HostContext } from '../../embed-script/HostContext';
import { postMessageToParent } from '../../libs/messaging';
import { AlertType } from '../../libs/models/AlertType';
import { CustomerLiveChatService } from '../../libs/services/CustomerLiveChatService';
import { downscaleImage, ImageTooLargeError, UnsupportedImageFormatError } from '../../libs/utils/imageUtils';
import { buildLocalImageEcho } from '../../libs/utils/localImageEcho';
import { CaptureCancelledError, captureDisplayFrame, isDisplayCaptureSupported } from '../../libs/utils/screenCapture';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { addMessageToConversationFromServer } from '../../redux/features/conversations/conversationsSlice';
import { clearScreenshotRequest } from '../../redux/features/livechat/screenshotRequestSlice';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        marginBottom: tokens.spacingVerticalXS,
        maxWidth: '900px',
        width: '100%',
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        boxSizing: 'border-box',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    text: {
        flexGrow: 1,
        fontSize: tokens.fontSizeBase300,
    },
    hint: {
        fontSize: '11px',
        color: tokens.colorNeutralForeground3,
        width: '100%',
    },
    hiddenInput: {
        display: 'none',
    },
});

interface ScreenshotRequestPromptProps {
    chatId: string;
    token: string | undefined;
    hostContext: HostContext | undefined;
}

type PromptState = 'idle' | 'capturing' | 'uploading';

/**
 * Transient "the agent asked for a screenshot" band above the composer. Shown while the
 * screenshotRequest slice holds a request for this chat; the customer either shares their screen
 * (native picker), attaches an image file where screen capture is unsupported, or dismisses.
 * A sent screenshot is echoed locally like a pasted image (never re-fetched after reload).
 */
export const ScreenshotRequestPrompt: React.FC<ScreenshotRequestPromptProps> = ({ chatId, token, hostContext }) => {
    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const activeUserInfo = useAppSelector((state: RootState) => state.app.activeUserInfo);
    const embeddedMode = useAppSelector((state: RootState) => state.app.embeddedMode);
    const [state, setState] = useState<PromptState>('idle');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const captureSupported = isDisplayCaptureSupported();

    // Reset any in-flight state when the prompt is re-used for another chat.
    useEffect(() => {
        setState('idle');
    }, [chatId]);

    // "Not now" clears the prompt immediately and tells the backend best-effort, so the agent gets a
    // "declined" notice rather than waiting on a dismissed prompt. A failed decline call is not worth
    // surfacing to the customer.
    const dismiss = (): void => {
        dispatch(clearScreenshotRequest({ chatId }));
        if (token) {
            void new CustomerLiveChatService(token).declineScreenshotAsync(chatId, hostContext).catch(() => undefined);
        }
    };

    const reportImageError = (err: unknown): void => {
        const messageKey =
            err instanceof ImageTooLargeError
                ? 'chatInput.imageTooLarge'
                : err instanceof UnsupportedImageFormatError
                  ? 'chatInput.imageUnsupportedFormat'
                  : 'screenshotRequest.errorGeneric';
        dispatch(addAlert({ type: AlertType.Warning, message: t(messageKey) }));
    };

    const send = async (blob: Blob): Promise<void> => {
        if (!token) {
            dispatch(addAlert({ type: AlertType.Warning, message: t('screenshotRequest.errorGeneric') }));
            setState('idle');
            return;
        }
        setState('uploading');
        try {
            const { base64, mimeType } = await downscaleImage(blob, undefined, { stripMetadata: true });
            await new CustomerLiveChatService(token).uploadScreenshotAsync(
                chatId,
                { base64, mimeType, requested: true },
                hostContext,
            );

            const echo = buildLocalImageEcho(
                chatId,
                activeUserInfo,
                base64,
                mimeType,
                t('screenshotRequest.sentCaption'),
            );
            dispatch(addMessageToConversationFromServer({ message: echo, chatId }));
            dispatch(clearScreenshotRequest({ chatId }));
        } catch (err: unknown) {
            reportImageError(err);
            setState('idle');
        }
    };

    const handleShareScreen = async (): Promise<void> => {
        setState('capturing');
        let frame: Blob;
        // Embedded: steer the picker to the host tab and have the host hide the widget for the
        // frame grab itself (not while the picker is open), so the agent sees the application
        // rather than the chat panel over it.
        try {
            frame = await captureDisplayFrame({
                preferCurrentTab: embeddedMode,
                onSurfaceSelected: () => {
                    postMessageToParent({ event: 'screenshotCapture', data: { phase: 'start' } });
                },
            });
        } catch (err: unknown) {
            if (!(err instanceof CaptureCancelledError)) {
                dispatch(addAlert({ type: AlertType.Warning, message: t('screenshotRequest.errorGeneric') }));
            }
            setState('idle');
            return;
        } finally {
            postMessageToParent({ event: 'screenshotCapture', data: { phase: 'end' } });
        }
        await send(frame);
    };

    const handleFilePicked = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        await send(file);
    };

    const busy = state !== 'idle';
    const statusKey = state === 'capturing' ? 'screenshotRequest.capturing' : 'screenshotRequest.uploading';

    return (
        <div className={classes.root} role="status" data-testid="screenshot-request-prompt">
            <span className={classes.text}>
                <strong>{t('screenshotRequest.title')}</strong> {t('screenshotRequest.body')}
            </span>
            {captureSupported && (
                <Button
                    appearance="primary"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                        void handleShareScreen();
                    }}
                >
                    {busy ? t(statusKey) : t('screenshotRequest.share')}
                </Button>
            )}
            {/* Always offered: a screen picker the host blocks fails like a dismissed picker, so
                the customer needs a way to answer that does not depend on it. */}
            <Button
                appearance={captureSupported ? 'secondary' : 'primary'}
                size="small"
                disabled={busy}
                onClick={() => {
                    fileInputRef.current?.click();
                }}
            >
                {busy && !captureSupported ? t(statusKey) : t('screenshotRequest.attach')}
            </Button>
            <Button appearance="subtle" size="small" disabled={busy} onClick={dismiss}>
                {t('screenshotRequest.dismiss')}
            </Button>
            {!captureSupported && <span className={classes.hint}>{t('screenshotRequest.unsupportedHint')}</span>}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className={classes.hiddenInput}
                aria-label={t('screenshotRequest.attach')}
                onChange={(e) => {
                    void handleFilePicked(e);
                }}
            />
        </div>
    );
};
